import { FormControl, FormGroup, Validators } from '@angular/forms';

import { ErrorCode, ErrorType } from '@constants/error.constant';
import { ProspectAcquisitionChannel } from '@gi/enums/prospect-acquisition-channel.enum';
import { ProspectSource } from '@gi/enums/prospect-source.enum';
import { TiimeBusinessProAccountIntention } from '@gi/enums/tiime-business-pro-account-intention.enum';
import { Contract } from '@gi/models/contract';
import { Prospect } from '@gi/models/prospect';
import { DateHelper } from '@helpers/date.helper';
import { FormHelper } from '@helpers/form.helper';
import { PhoneHelper } from '@helpers/phone.helper';
import { ValidatorHelper } from '@helpers/validators.helper';
import { ErrorMessage } from '@interfaces/error-message';
import { BusinessUnit } from '@models/business-unit';
import { BusinessUser } from '@models/business-user';
import { Company } from '@models/company';
import { Contributor } from '@models/contributor';

export type ProspectFormType = {
  id: FormControl<number | undefined>;
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  civility: FormControl<string | null>;
  email: FormControl<string | null>;
  phone: FormControl<string | null>;
  mailingStreet: FormControl<string | null>;
  mailingPostalCode: FormControl<string | null>;
  mailingCity: FormControl<string | null>;
  internalComment: FormControl<string | null>;
  businessUnit: FormControl<BusinessUnit | null>;
  salesRepresentativeUser: FormControl<Contributor | null>;
  companyLegalForm: FormControl<string | null>;
  companyName: FormControl<string | null>;
  appointmentDate: FormControl<string | null>;
  appointmentTime: FormControl<string | null>;
  createdAt: FormControl<Date | null>;
  createdBy: FormControl<BusinessUser | null>;
  source: FormControl<ProspectSource | null>;
  acquisitionChannel: FormControl<ProspectAcquisitionChannel | null>;
  company: FormControl<Company | null>;
  tiimeBusinessProAccountIntention: FormControl<TiimeBusinessProAccountIntention | null>;
  contracts: FormControl<Contract[]>;
  complementChannel: FormControl<string | null>;
  caForecastAmount: FormControl<number | null>;
};

export class ProspectForm extends FormGroup<ProspectFormType> {
  get id(): FormControl<number | undefined> {
    return this.controls.id;
  }

  get firstName(): FormControl<string | null> {
    return this.controls.firstName;
  }

  get firstNameErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(this.firstName, 'Prénom requis');
  }

  get lastName(): FormControl<string | null> {
    return this.controls.lastName;
  }

  get lastNameErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(this.lastName, 'Nom requis');
  }

  get civility(): FormControl<string | null> {
    return this.controls.civility;
  }

  get email(): FormControl<string | null> {
    return this.controls.email;
  }

  get emailErrorMessage(): ErrorMessage {
    return FormHelper.composeErrorMessages(this.email, [
      { method: 'getEmptyErrorMessage', args: ['Email requis'] },
      {
        method: 'simpleErrorMessage',
        args: [
          {
            simpleErrorCode: ErrorType.EMAIL,
            simpleErrorMessage: 'Email invalide'
          }
        ]
      }
    ]);
  }

  get phone(): FormControl<string | null> {
    return this.controls.phone;
  }

  get phoneErrorMessage(): ErrorMessage {
    return FormHelper.composeErrorMessages(this.phone, [
      { method: 'getEmptyErrorMessage', args: ['Téléphone requis'] },
      {
        method: 'simpleErrorMessage',
        args: [
          {
            simpleErrorCode: ErrorType.INVALID_PHONE_NUMBER,
            simpleErrorMessage: 'Téléphone invalide'
          }
        ]
      }
    ]);
  }

  get mailingStreet(): FormControl<string | null> {
    return this.controls.mailingStreet;
  }

  get mailingPostalCode(): FormControl<string | null> {
    return this.controls.mailingPostalCode;
  }

  get mailingCity(): FormControl<string | null> {
    return this.controls.mailingCity;
  }

  get internalComment(): FormControl<string | null> {
    return this.controls.internalComment;
  }

  get businessUnit(): FormControl<BusinessUnit | null> {
    return this.controls.businessUnit;
  }

  get businessUnitErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(this.businessUnit, 'Groupe requis');
  }

  get salesRepresentativeUser(): FormControl<Contributor | null> {
    return this.controls.salesRepresentativeUser;
  }

  get companyLegalForm(): FormControl<string | null> {
    return this.controls.companyLegalForm;
  }

  get companyName(): FormControl<string | null> {
    return this.controls.companyName;
  }

  get companyNameErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(this.companyName, 'Nom entreprise requis');
  }

  get appointmentDate(): FormControl<string | null> {
    return this.controls.appointmentDate;
  }

  get appointmentTime(): FormControl<string | null> {
    return this.controls.appointmentTime;
  }

  get createdAt(): FormControl<Date | null> {
    return this.controls.createdAt;
  }

  get createdBy(): FormControl<BusinessUser | null> {
    return this.controls.createdBy;
  }

  get source(): FormControl<ProspectSource | null> {
    return this.controls.source;
  }

  get acquisitionChannel(): FormControl<ProspectAcquisitionChannel | null> {
    return this.controls.acquisitionChannel;
  }

  get acquisitionChannelErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(this.acquisitionChannel, `Canal d'acquisition requis`);
  }

  get company(): FormControl<Company | null> {
    return this.controls.company;
  }

  get tiimeBusinessProAccountIntention(): FormControl<TiimeBusinessProAccountIntention | null> {
    return this.controls.tiimeBusinessProAccountIntention;
  }

  get contracts(): FormControl<Contract[]> {
    return this.controls.contracts;
  }

  get complementChannel(): FormControl<string | null> {
    return this.controls.complementChannel;
  }

  get complementChannelErrorMessage(): ErrorMessage | null {
    return FormHelper.getMaxLengthErrorMessage(this.complementChannel);
  }

  get caForecastAmount(): FormControl<number | null> {
    return this.controls.caForecastAmount;
  }

  get caForecastAmountErrorMessage(): ErrorMessage | null {
    return FormHelper.getMinErrorMessage(
      this.caForecastAmount,
      'Le montant doit être supérieur à 0',
      ErrorCode.INVALID
    );
  }

  constructor() {
    super({
      id: new FormControl<number | undefined>(undefined),
      firstName: new FormControl<string | null>(null, Validators.required),
      lastName: new FormControl<string | null>(null, Validators.required),
      civility: new FormControl<string | null>(null),
      email: new FormControl<string | null>(null, [Validators.required, ValidatorHelper.emailValidator()]),
      phone: new FormControl<string | null>(null, [Validators.required, PhoneHelper.phoneNumberValidator()]),
      mailingStreet: new FormControl<string | null>(null),
      mailingPostalCode: new FormControl<string | null>(null),
      mailingCity: new FormControl<string | null>(null),
      internalComment: new FormControl<string | null>(null),
      businessUnit: new FormControl<BusinessUnit | null>(null, Validators.required),
      salesRepresentativeUser: new FormControl<Contributor | null>(null),
      companyLegalForm: new FormControl<string | null>(null),
      companyName: new FormControl<string | null>(null, Validators.required),
      appointmentDate: new FormControl<string | null>(null),
      appointmentTime: new FormControl<string | null>(null),
      createdAt: new FormControl<Date | null>(null),
      createdBy: new FormControl<BusinessUser | null>(null),
      source: new FormControl<ProspectSource | null>(null),
      acquisitionChannel: new FormControl<ProspectAcquisitionChannel | null>(null, Validators.required),
      company: new FormControl<Company | null>(null),
      tiimeBusinessProAccountIntention: new FormControl<TiimeBusinessProAccountIntention | null>(null),
      contracts: new FormControl<Contract[]>([]),
      complementChannel: new FormControl<string | null>(null, Validators.maxLength(255)),
      caForecastAmount: new FormControl<number | null>(null, Validators.min(0))
    });
  }

  fromProspect(prospect: Prospect): void {
    this.patchValue({
      id: prospect.id,
      lastName: prospect.lastName,
      firstName: prospect.firstName,
      civility: prospect.civility,
      phone:
        prospect.phone && PhoneHelper.isValid(prospect.phone) ? PhoneHelper.format(prospect.phone) : prospect.phone,
      email: prospect.email,
      mailingStreet: prospect.mailingStreet,
      mailingPostalCode: prospect.mailingPostalCode,
      mailingCity: prospect.mailingCity,
      internalComment: prospect.internalComment,
      businessUnit: prospect.businessUnit,
      salesRepresentativeUser: prospect.salesRepresentativeUser,
      companyLegalForm: prospect.companyLegalForm,
      companyName: prospect.companyName,
      appointmentDate: prospect.appointmentDate ? DateHelper.format(prospect.appointmentDate) : null,
      appointmentTime: prospect.appointmentDate ? DateHelper.format(prospect.appointmentDate, 'HH:mm') : null,
      createdAt: prospect.createdAt,
      createdBy: prospect.createdBy,
      source: prospect.source,
      acquisitionChannel: prospect.acquisitionChannel,
      company: prospect.company,
      tiimeBusinessProAccountIntention: prospect.tiimeBusinessProAccountIntention,
      contracts: prospect.contracts,
      complementChannel: prospect.complementChannel,
      caForecastAmount: prospect.caForecastAmount
    });
  }

  toProspect(): Prospect {
    const prospect = new Prospect();

    prospect.id = this.id.value;
    prospect.lastName = this.lastName.value;
    prospect.firstName = this.firstName.value;
    prospect.civility = this.civility.value;
    prospect.phone = this.phone.value;
    prospect.email = this.email.value;
    prospect.mailingStreet = this.mailingStreet.value;
    prospect.mailingPostalCode = this.mailingPostalCode.value;
    prospect.mailingCity = this.mailingCity.value;
    prospect.internalComment = this.internalComment.value;
    prospect.businessUnit = this.businessUnit.value;
    prospect.salesRepresentativeUser = this.salesRepresentativeUser.value;
    prospect.companyLegalForm = this.companyLegalForm.value;
    prospect.companyName = this.companyName.value;
    prospect.appointmentDate = this.toProspectAppointmentDate();
    prospect.acquisitionChannel = this.acquisitionChannel.value;
    prospect.tiimeBusinessProAccountIntention = this.tiimeBusinessProAccountIntention.value;
    prospect.complementChannel = this.complementChannel.value;
    prospect.company = this.company.value;
    prospect.caForecastAmount = this.caForecastAmount.value;

    return prospect;
  }

  private toProspectAppointmentDate(): string {
    if (this.appointmentDate.value && this.appointmentTime.value) {
      const splitTime = this.appointmentTime.value.split(':');
      const time = new Date();
      time.setHours(Number(splitTime[0]));
      time.setMinutes(Number(splitTime[1]));

      return `${DateHelper.format(this.appointmentDate.value)} ${DateHelper.format(time, 'HH:mm')}`;
    }

    if (this.appointmentDate.value) {
      return DateHelper.format(this.appointmentDate.value);
    }

    return null;
  }
}
